import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BucketTwrFormula } from "constants/index";
import { twrFormulaLabel } from "./summaryFigures";

const OPTION_LABEL_SX = {
  mr: 0,
  "& .MuiFormControlLabel-label": {
    typography: "body2",
  },
};

// What the Option Pairing switch does, for the (i) beside its heading: a
// plain-language reading of the two open-position rules in the backend's
// asset_option_transaction (filter_expired_transactions, the previous
// formula, and _filter_open_positions, the fixed pairing of task 3869).
// Re-read both when either changes -- this copy restates them.
const OPTION_PAIRING_HELP = (
  <>
    <Typography variant="body2" sx={{ mb: 1 }}>
      Decides which options count as still open after their last trade, and so
      carry a market value in the report&apos;s return calculation.
    </Typography>
    <Typography variant="body2" sx={{ mb: 1 }}>
      <strong>Fixed pairing (on):</strong> each option is read by its own type
      &mdash; a CALL opens on a sell and closes on a buy, a PUT opens on a buy
      and closes on a sell. A bought PUT still open, or held to expiry, is
      valued until its expiry date; a PUT sold to open is not counted as open.
    </Typography>
    <Typography variant="body2" sx={{ mb: 1 }}>
      <strong>Previous formula (off):</strong> an option counts as open only
      when it is net sold &mdash; sold CALLs and sold PUTs alike &mdash; so
      bought PUTs still open or held to expiry are left out.
    </Typography>
    <Typography variant="body2">
      Changes the market value given to open options, and with it the returns
      and option figures calculated from it. Starting and ending values come
      from the account&apos;s positions and do not change.
    </Typography>
  </>
);

/**
 * The report's formula settings: which time-weighted return formula the
 * cards show, and the option-pairing formula the next Calculate runs with.
 * Rendered inside the report's Settings dialog (StrategyReportSettings).
 *
 * Disabled for the whole of a run, so neither can change under one.
 */
const SummaryFormulaSettings = ({
  disabled,
  twrFormula,
  onTwrFormulaChange,
  optionPairingV2,
  onOptionPairingChange,
  periodStart,
}: {
  disabled: boolean;
  twrFormula: BucketTwrFormula;
  onTwrFormulaChange: (formula: BucketTwrFormula) => void;
  optionPairingV2: boolean;
  onOptionPairingChange: (enabled: boolean) => void;
  periodStart: string | undefined;
}) => (
  <>
    <FormControl disabled={disabled}>
      <FormLabel id="twr-formula-label" sx={{ mb: 1, typography: "subtitle2" }}>
        Time-Weighted Return
      </FormLabel>
      <RadioGroup
        aria-labelledby="twr-formula-label"
        value={twrFormula}
        onChange={(event) =>
          onTwrFormulaChange(event.target.value as BucketTwrFormula)
        }
      >
        {Object.values(BucketTwrFormula).map((formula) => (
          <FormControlLabel
            key={formula}
            value={formula}
            control={<Radio size="small" />}
            label={twrFormulaLabel(formula, periodStart)}
            sx={OPTION_LABEL_SX}
          />
        ))}
      </RadioGroup>
    </FormControl>
    <FormControl disabled={disabled} sx={{ mt: 2, display: "block" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
        <FormLabel id="option-pairing-label" sx={{ typography: "subtitle2" }}>
          Option Pairing
        </FormLabel>
        <Tooltip
          title={OPTION_PAIRING_HELP}
          // Above the heading rather than below or beside it, so the switch
          // it explains stays in view while it is read.
          placement="top-start"
          // Describes the button rather than naming it, so it keeps its
          // "About Option Pairing" name while the explanation is open.
          describeChild
          enterTouchDelay={0}
          leaveTouchDelay={15000}
          slotProps={{
            tooltip: { sx: { maxWidth: 360, p: 1.5 } },
            // Always above: popper's flip misjudges the room there on open
            // and drops it onto the switch. preventOverflow, on both axes,
            // slides it back on screen where the room above is short.
            popper: {
              modifiers: [
                { name: "flip", enabled: false },
                {
                  name: "preventOverflow",
                  options: { altAxis: true, tether: false, padding: 8 },
                },
              ],
            },
          }}
        >
          <IconButton
            aria-label="About Option Pairing"
            size="small"
            sx={{ ml: 0.5, p: 0.25 }}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={optionPairingV2}
            onChange={(event) => onOptionPairingChange(event.target.checked)}
            inputProps={{ "aria-labelledby": "option-pairing-label" }}
          />
        }
        label={
          optionPairingV2 ? "Fixed pairing (PUT expiry)" : "Previous formula"
        }
        sx={OPTION_LABEL_SX}
      />
      <Typography variant="caption" color="text.secondary" display="block">
        {optionPairingV2
          ? "PUTs count as open when bought, CALLs when sold."
          : "Only net-sold options, CALL or PUT, count as open."}{" "}
        Applies on the next Calculate.
      </Typography>
    </FormControl>
  </>
);

export default SummaryFormulaSettings;
